import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, Navigate } from 'react-router-dom';
import { db } from '../firebase';
import { 
  getAuth, 
  reauthenticateWithCredential, 
  reauthenticateWithPopup,
  EmailAuthProvider, 
  GoogleAuthProvider 
} from 'firebase/auth';
import { doc, deleteDoc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';

const Profile = () => {
  const { currentUser, loading: authLoading, logout, deleteUser, setCurrentUser } = useAuth();
  const location = useLocation();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  
  useEffect(() => {
    console.log('Profile mounted:', {
      path: location.pathname,
      currentUser,
      authLoading
    });
  }, [location.pathname, currentUser, authLoading]);

  // If not logged in, redirect to home
  if (!authLoading && !currentUser) {
    return <Navigate to="/" replace />;
  }

  // Show loading state only if we're still checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const handleCancelSubscription = async () => {
    try {
      console.log('Starting subscription cancellation...');
      setLoading(true);
      setError('');
      
      // Call the Firebase function
      console.log('Calling cancelSubscription function...');
      const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
      const result = await cancelSubscription({});
      
      console.log('Function call result:', result);
      
      if (result?.data?.success) {
        console.log('Subscription cancelled successfully, updating user state...');
        // Force a refresh of the auth state to update premium status
        const auth = getAuth();
        await auth.currentUser.reload();
        
        // Get the latest premium status from Firestore
        const premiumDoc = await getDoc(doc(db, 'premiumUsers', currentUser.uid));
        const isPremium = premiumDoc.exists() && premiumDoc.data()?.subscriptionActive === true;
        console.log('New premium status:', isPremium);
        
        // Update the currentUser state through the auth context
        setCurrentUser(prevUser => {
          console.log('Updating currentUser state:', { prevUser, isPremium });
          return {
            ...prevUser,
            isPremium
          };
        });

        // Show success message
        console.log('Setting success message...');
        setError('Subscription cancelled successfully. You will no longer be charged.');
      } else {
        console.log('Subscription cancellation failed:', result);
        setError('Failed to cancel subscription. Please try again.');
      }
    } catch (error) {
      console.error('Error in handleCancelSubscription:', error);
      setError(error.message || 'Failed to cancel subscription');
    } finally {
      console.log('Cleaning up...');
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      setError('');
      
      const auth = getAuth();
      
      // Reauthenticate based on provider
      if (currentUser.providerData[0].providerId === 'google.com') {
        // For Google users, use GoogleAuthProvider and reauthenticateWithPopup
        const provider = new GoogleAuthProvider();
        await reauthenticateWithPopup(auth.currentUser, provider);
      } else {
        // For email/password users, show password modal
        setShowPasswordModal(true);
        return; // Exit early, will continue after password confirmation
      }
      
      // If we get here, we're either a Google user or have confirmed password
      await proceedWithDeletion();
    } catch (error) {
      console.error('Error during reauthentication:', error);
      setError(error.message || 'Failed to reauthenticate');
      setLoading(false);
    }
  };

  const proceedWithDeletion = async () => {
    try {
      // Delete user data from Firestore
      if (currentUser.isPremium) {
        await deleteDoc(doc(db, 'premiumUsers', currentUser.uid));
      }
      
      // Delete the user account
      await deleteUser();
      
      // Logout will be handled by the AuthContext
    } catch (error) {
      console.error('Error deleting account:', error);
      setError(error.message || 'Failed to delete account');
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      
      // Check if user is signed in
      if (!currentUser) {
        setError('No user is currently signed in.');
        return;
      }

      // Log the current user for debugging
      console.log('Current user:', currentUser);
      
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      
      // Log the credential for debugging
      console.log('Created credential:', credential);
      
      // Reauthenticate using the modular Firebase SDK
      const auth = getAuth();
      await reauthenticateWithCredential(auth.currentUser, credential);
      
      setShowPasswordModal(false);
      await proceedWithDeletion();
    } catch (error) {
      console.error('Error reauthenticating with password:', error);
      setError('Invalid password. Please try again.');
      setPassword('');
    }
  };

  // Add this function to handle trial cancellation
  const handleCancelTrial = async () => {
    try {
      setLoading(true);
      setError('');
      // Call the backend to cancel the Stripe subscription
      const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
      const result = await cancelSubscription({});
      if (result?.data?.success) {
        // Optionally update Firestore/UI here if needed
        if (typeof setCurrentUser === 'function') {
          setCurrentUser(prevUser => ({
            ...prevUser,
            isTrial: false
          }));
        }
        setError('');
        console.log('Trial cancelled successfully.');
      } else {
        setError('Failed to cancel trial. Please try again.');
      }
    } catch (error) {
      setError('Failed to cancel trial. Please try again.');
      console.error('Error cancelling trial:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if trial is active
  const isTrialActive = currentUser.isTrial;

  return (
    <div className="relative isolate px-6 py-10 sm:py-14 lg:px-8">
      <div aria-hidden="true" className="absolute inset-x-0 -top-3 -z-10 transform-gpu overflow-hidden px-36 blur-3xl">
        <div
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
          className="mx-auto aspect-1155/678 w-[72.1875rem] bg-gradient-to-tr from-[#4ade80] to-[#22c55e] opacity-30"
        />
      </div>
      <div className="mx-auto max-w-4xl text-center">
        <h2 className="text-base font-semibold text-slate-900">Profile</h2>
        <p className="mt-2 text-5xl font-semibold tracking-tight text-gray-900 sm:text-6xl">
          Account Settings
        </p>
      </div>
      <div className="mx-auto mt-16 max-w-lg">
        <div className="bg-white rounded-3xl p-8 ring-1 ring-gray-900/10 sm:p-10">
          {error && (
            <div className="mb-4 tiu-alert-error" role="alert">
              {error}
            </div>
          )}
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <div className="mt-1 text-gray-900">{currentUser.email}</div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Type</label>
              <div className="mt-1">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  currentUser.isPremium
                    ? 'bg-green-100 text-green-800'
                    : isTrialActive
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
                }`}>
                  {currentUser.isPremium
                    ? 'Premium Member'
                    : isTrialActive
                      ? 'Free Trial'
                      : 'Free Account'}
                </span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Created</label>
              <div className="mt-1 text-gray-900">
                {new Date(currentUser.metadata.creationTime).toLocaleDateString()}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Sign In</label>
              <div className="mt-1 text-gray-900">
                {new Date(currentUser.metadata.lastSignInTime).toLocaleString()}
              </div>
            </div>
            
            {/* Cancel Subscription or Trial Button */}
            {currentUser.isPremium && (
              <div className="pt-4">
                <button
                  onClick={handleCancelSubscription}
                  disabled={loading}
                  className="w-full rounded-md bg-red-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    'Cancel Subscription'
                  )}
                </button>
              </div>
            )}
            {/* Cancel Trial Button */}
            {isTrialActive && !currentUser.isPremium && (
              <div className="pt-4">
                <button
                  onClick={handleCancelTrial}
                  disabled={loading}
                  className="w-full rounded-md bg-yellow-600 px-3.5 py-2.5 text-center text-sm font-semibold text-slate-900 shadow-sm hover:bg-yellow-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    'Cancel Trial'
                  )}
                </button>
              </div>
            )}
            
            <div className="pt-4">
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full rounded-md bg-gray-600 px-3.5 py-2.5 text-center text-sm font-semibold text-slate-900 shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                >
                  Delete Account
                </button>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Are you sure?</h3>
                        <div className="mt-2 text-sm text-red-700">
                          <p>This action cannot be undone. All your data will be permanently deleted.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-4">
                    <button
                      onClick={handleDeleteAccount}
                      disabled={loading}
                      className="flex-1 rounded-md bg-red-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Deleting...
                        </div>
                      ) : (
                        'Yes, Delete Account'
                      )}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 rounded-md bg-gray-600 px-3.5 py-2.5 text-center text-sm font-semibold text-slate-900 shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Password Confirmation Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Password</h3>
            <form onSubmit={handlePasswordSubmit}>
              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPassword('');
                    setError('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Confirm
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile; 